<script lang="ts">
  import type { Conversation } from '../lib/api';
  import { conversationPath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let { onNew, onLogout }: { onNew: () => void; onLogout: () => void } = $props();

  let showArchived = $state(false);

  const active = $derived(app.conversations.filter((c) => !c.archived_at));
  // Projects stay in their own section, before the channels, unread or not (phase 2).
  const projects = $derived(active.filter((c) => c.is_project).sort((a, b) => a.title.localeCompare(b.title, 'fr')));
  const unread = $derived(active.filter((c) => !c.is_project && (c.unread_count > 0 || c.mention_count > 0)));
  const isChannel = (c: Conversation) => c.kind === 'public' || c.kind === 'private';
  const channels = $derived(active.filter((c) => isChannel(c) && !c.is_project && !unread.includes(c)));
  const myTurn = $derived(app.taskSummary?.my_turn ?? 0);
  const directs = $derived(active.filter((c) => !isChannel(c) && !unread.includes(c)));
  const archived = $derived(
    app.conversations.filter((c) => c.archived_at).sort((a, b) => a.title.localeCompare(b.title, 'fr')),
  );

  function other(c: Conversation) {
    return c.members.find((m) => m.id !== app.me?.id) ?? c.members[0];
  }

  function open(c: Conversation, event: MouseEvent) {
    event.preventDefault();
    router.go(conversationPath(c.id));
  }

  const myPresence = $derived(app.me ? app.presence[app.me.id] : undefined);
</script>

{#snippet item(c: Conversation)}
  {@const counter = c.mention_count > 0 ? c.mention_count : c.unread_count}
  <a
    href={conversationPath(c.id)}
    class="item"
    class:unread={counter > 0}
    aria-current={app.openId === c.id ? 'page' : undefined}
    onclick={(e) => open(c, e)}
  >
    {#if c.is_project}
      <span class="hash project" aria-hidden="true">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M9 4v16M15 4v10" /></svg>
      </span>
    {:else if c.kind === 'direct'}
      {@const person = other(c)}
      {#if person}<Avatar user={person} size={20} presence={app.presence[person.id]} />{/if}
    {:else if c.kind === 'group'}
      <span class="group-icon" aria-hidden="true">{c.members.length}</span>
    {:else}
      <span class="hash" aria-hidden="true">
        {#if c.kind === 'private'}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></svg>
        {:else}#{/if}
      </span>
    {/if}
    <span class="name">{c.title}</span>
    {#if counter > 0}
      <span class="badge" class:mention={c.mention_count > 0}>
        <span class="sr-only">{c.mention_count > 0 ? 'mentions' : 'non lus'} :</span>{counter}
      </span>
    {:else if c.is_project && c.open_task_count > 0}
      <span class="open-tasks" title="Tâches ouvertes"><span class="sr-only">tâches ouvertes :</span>{c.open_task_count}</span>
    {/if}
  </a>
{/snippet}

<nav class="sidebar" aria-label="Conversations">
  <div class="brand">
    <span class="logo" aria-hidden="true">{(app.info?.app_name ?? 'M').charAt(0)}</span>
    <div class="brand-text">
      <span class="app">{app.info?.app_name ?? 'Messagerie'}</span>
      <span class="org">Facilogi</span>
    </div>
    <button type="button" class="icon-btn" aria-label="Nouvelle conversation" title="Nouvelle conversation" onclick={onNew}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
    </button>
  </div>
  <div class="search">
    <button
      type="button"
      class="search-btn"
      aria-current={router.route.name === 'search' ? 'page' : undefined}
      onclick={() => router.go('/search')}
    >
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
      <span>Rechercher</span><kbd>Ctrl K</kbd>
    </button>
  </div>

  <div class="shortcuts">
    {#if projects.length > 0 || app.myTasks || myTurn > 0}
      <a
        href="/tasks"
        class="item shortcut"
        aria-current={router.route.name === 'tasks' ? 'page' : undefined}
        onclick={(e) => {
          e.preventDefault();
          router.go('/tasks');
        }}
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="17" rx="3" /><path d="M8 11l2.5 2.5L16 8" /></svg>
        <span class="name">Mes tâches</span>
        {#if myTurn > 0}<span class="badge"><span class="sr-only">à moi d'agir :</span>{myTurn}</span>{/if}
      </a>
    {/if}
    <a
      href="/saved"
      class="item shortcut"
      aria-current={router.route.name === 'saved' ? 'page' : undefined}
      onclick={(e) => {
        e.preventDefault();
        router.go('/saved');
      }}
    >
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M6 3h12v18l-6-4-6 4z" /></svg>
      <span class="name">Messages enregistrés</span>
    </a>
    {#if app.me?.is_admin}
      <a
        href="/admin"
        class="item shortcut"
        aria-current={router.route.name === 'admin' ? 'page' : undefined}
        onclick={(e) => {
          e.preventDefault();
          router.go('/admin');
        }}
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M17 11h5M19.5 8.5v5" /></svg>
        <span class="name">Administration</span>
      </a>
    {/if}
  </div>

  <div class="sections">
    {#if unread.length > 0}
      <section aria-labelledby="s-unread">
        <h2 id="s-unread">Non lus</h2>
        {#each unread as c (c.id)}{@render item(c)}{/each}
      </section>
    {/if}
    {#if projects.length > 0}
      <section aria-labelledby="s-projects">
        <h2 id="s-projects">Projets</h2>
        {#each projects as c (c.id)}{@render item(c)}{/each}
      </section>
    {/if}
    <section aria-labelledby="s-channels">
      <h2 id="s-channels">Canaux</h2>
      {#each channels as c (c.id)}{@render item(c)}{:else}<p class="empty">Aucun canal</p>{/each}
    </section>
    <section aria-labelledby="s-directs">
      <h2 id="s-directs">Messages directs</h2>
      {#each directs as c (c.id)}{@render item(c)}{:else}<p class="empty">Aucun message direct</p>{/each}
    </section>
    {#if archived.length > 0}
      <section aria-labelledby="s-archived">
        <h2 id="s-archived">
          <button type="button" class="toggle" aria-expanded={showArchived} onclick={() => (showArchived = !showArchived)}>
            <span aria-hidden="true">{showArchived ? '▾' : '▸'}</span> Canaux archivés · {archived.length}
          </button>
        </h2>
        {#if showArchived}
          {#each archived as c (c.id)}{@render item(c)}{/each}
        {/if}
      </section>
    {/if}
  </div>

  {#if app.me}
    <div class="me">
      <a
        class="me-link"
        href="/settings/profile"
        title="Profil : nom et photo"
        aria-current={router.route.name === 'profile' ? 'page' : undefined}
        onclick={(e) => {
          e.preventDefault();
          router.go('/settings/profile');
        }}
      >
        <Avatar user={app.me} size={28} presence={myPresence} />
        <span class="me-text">
          <span class="me-name">{app.me.display_name}</span>
          <span class="me-status">{app.connected ? (myPresence === 'online' ? 'Disponible' : 'Absent') : 'Reconnexion…'}</span>
        </span>
      </a>
      <a
        href="/settings/security"
        class="icon-btn"
        aria-label="Sécurité du compte"
        title="Sécurité du compte"
        aria-current={router.route.name === 'security' ? 'page' : undefined}
        onclick={(e) => {
          e.preventDefault();
          router.go('/settings/security');
        }}
      >
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
      </a>
      <button type="button" class="icon-btn" aria-label="Se déconnecter" title="Se déconnecter" onclick={onLogout}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" /></svg>
      </button>
    </div>
  {/if}
</nav>

<style>
  .sidebar {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-sidebar);
    border-right: 1px solid var(--border);
    min-width: 0;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 12px 12px 16px;
  }
  .logo {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-m);
    background: var(--accent);
    color: #ffffff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .brand-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .app {
    font-weight: 600;
    font-size: 15px;
  }
  .org {
    font-size: 12px;
    line-height: 16px;
    color: var(--muted);
  }
  .icon-btn {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .icon-btn:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .icon-btn[aria-current='page'] {
    color: var(--accent);
  }
  .search {
    padding: 0 12px 12px;
  }
  .search-btn {
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    background: var(--bg);
    color: var(--muted);
  }
  .search-btn span {
    flex: 1;
    text-align: left;
  }
  .search-btn:hover {
    border-color: var(--border-strong);
    color: var(--ink-2);
  }
  .search-btn[aria-current='page'] {
    border-color: var(--accent-border);
    background: var(--accent-soft);
    color: var(--accent-on-soft);
  }
  kbd {
    font: inherit;
    font-size: 12px;
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0 4px;
    line-height: 18px;
  }
  .open-tasks {
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }
  .hash.project {
    color: var(--muted);
  }
  .shortcuts {
    padding: 0 8px 8px;
  }
  .shortcut {
    min-height: 36px;
    color: var(--ink);
    font-weight: 600;
  }
  .shortcut svg {
    color: var(--muted);
  }
  .sections {
    flex: 1;
    overflow-y: auto;
    padding: 0 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  section {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  h2 {
    margin: 0;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--muted);
    padding: 4px 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .toggle {
    border: 0;
    background: none;
    padding: 0;
    color: inherit;
    font: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
  }
  .item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 0 8px;
    border-radius: var(--radius-s);
    color: var(--ink-2);
    text-decoration: none;
  }
  .item:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .item.unread {
    color: var(--ink);
    font-weight: 600;
  }
  .item[aria-current='page'] {
    background: var(--accent-selected);
    color: var(--ink);
    font-weight: 600;
  }
  .item[aria-current='page'] .hash {
    color: var(--accent);
  }
  .hash {
    width: 20px;
    text-align: center;
    color: var(--muted);
    font-size: 13px;
  }
  .group-icon {
    width: 20px;
    height: 20px;
    border-radius: var(--radius-s);
    background: var(--border-strong);
    color: var(--ink-2);
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .badge {
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    background: var(--accent);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
  }
  .empty {
    margin: 0;
    padding: 4px 8px;
    color: var(--muted);
    font-size: 13px;
  }
  .me {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 12px 12px 16px;
    border-top: 1px solid var(--border);
  }
  .me-link {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: -6px 0 -6px -8px;
    padding: 6px 8px;
    border-radius: var(--radius-m);
    color: inherit;
    text-decoration: none;
  }
  .me-link:hover,
  .me-link[aria-current='page'] {
    background: var(--bg-hover);
  }
  .me-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .me-name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .me-status {
    font-size: 12px;
    line-height: 16px;
    color: var(--muted);
  }
  @media (max-width: 759px) {
    .item,
    .icon-btn {
      min-height: 44px;
    }
    .icon-btn {
      width: 44px;
    }
  }
</style>
